"use client";

import { useMemo, useEffect, type ReactNode } from "react";
import {
  getFontPairing,
  buildBrandCSS,
  type BrandThemeConfig,
} from "./theme-config";

// ---------------------------------------------------------------------------
// Google Font Loader
// ---------------------------------------------------------------------------

function loadGoogleFont(fontFamily: string): HTMLLinkElement | null {
  if (typeof document === "undefined") return null;

  const cleanName = fontFamily
    .split(",")[0]
    .trim()
    .replace(/^['"]|['"]$/g, "");

  const id = `google-font-${cleanName.replace(/\s+/g, "-").toLowerCase()}`;
  if (document.getElementById(id)) return null;

  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(cleanName)}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
  return link;
}

// ---------------------------------------------------------------------------
// BrandThemeProvider Component
// ---------------------------------------------------------------------------

/**
 * Shared theme provider that injects `--brand-*` CSS custom properties via a
 * `<style>` tag. SSR-safe — no DOM access outside `useEffect`.
 */
export function BrandThemeProvider({
  config,
  children,
}: {
  config: BrandThemeConfig | null;
  children: ReactNode;
}) {
  const cssOverrides = useMemo(
    () => (config ? buildBrandCSS(config) : ""),
    [config],
  );

  // Load custom Google Fonts client-side
  useEffect(() => {
    if (!config) return;

    const pairing = getFontPairing(config.fontPairing || "noto-serif-manrope");
    const fontHeading = config.fontHeading || pairing.primary;
    const fontBody = config.fontBody || pairing.secondary;

    loadGoogleFont(fontHeading);

    if (fontBody !== fontHeading) {
      loadGoogleFont(fontBody);
    }
  }, [config]);

  return (
    <>
      {cssOverrides && (
        <style dangerouslySetInnerHTML={{ __html: cssOverrides }} />
      )}
      {children}
    </>
  );
}
